import { useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ApiError, passwordReset } from '../api';

export default function ResetPasswordPage() {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get('token') ?? '';
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isPending, setIsPending] = useState(false);

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
        <div className="max-w-sm w-full bg-white rounded-lg shadow p-8 text-center space-y-3">
          <h1 className="text-2xl font-bold text-slate-900">{t('reset.invalidTitle')}</h1>
          <p className="text-slate-600">{t('reset.invalidBody')}</p>
          <Link to="/forgot-password" className="text-slate-600 hover:underline">
            {t('reset.requestNew')}
          </Link>
        </div>
      </div>
    );
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setIsPending(true);
    try {
      await passwordReset.reset(token, password);
      navigate('/login?reset=ok');
    } catch (err) {
      if (err instanceof ApiError) {
        setError(t([`error.${err.message}`, 'error.RESET_FAILED']));
      } else {
        setError(t('error.UNKNOWN'));
      }
      setIsPending(false);
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
      <form
        onSubmit={handleSubmit}
        className="max-w-sm w-full bg-white rounded-lg shadow p-8 space-y-4"
      >
        <h1 className="text-2xl font-bold text-slate-900">{t('reset.title')}</h1>

        {error && <div className="rounded bg-red-50 text-red-700 px-3 py-2 text-sm">{error}</div>}

        <label className="block">
          <span className="text-sm font-medium text-slate-700">{t('field.newPassword')}</span>
          <input
            type="password"
            required
            minLength={8}
            autoFocus
            autoComplete="new-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="mt-1 block w-full rounded border border-slate-300 px-3 py-2 focus:border-slate-500 focus:outline-none focus:ring-1 focus:ring-slate-500"
          />
          <span className="text-xs text-slate-500">{t('field.passwordHint')}</span>
        </label>

        <button
          type="submit"
          disabled={isPending}
          className="w-full bg-slate-900 text-white rounded px-4 py-2 hover:bg-slate-800 disabled:opacity-50"
        >
          {isPending ? t('common.working') : t('reset.submit')}
        </button>
      </form>
    </div>
  );
}
